import { useMemo, useState } from "react";
import { HeartPulse, X, Check, RotateCcw, Trophy, PlayCircle } from "lucide-react";

import { Input } from "@/components/ui/input";
import { useBingoGameStore } from "@/plugins/bingo/state/bingoGameStore";

/**
 * BingoCheckPanel — host-facing control room for the calm heartbeat
 * verification mode.
 *
 * Idle state:
 *   • The host clicks "Call Bingo Check", picks the numbers from the
 *     CALLED HISTORY that match the winner's claim (min 3, max 15),
 *     optionally enters a player label, then hits "Begin Verification".
 *
 * Active state:
 *   • Shows one big "Confirm Next" button (works via click OR spacebar
 *     — bound in useVerifyHotkey below).
 *   • Also shows the current progress (n / total), the pending number,
 *     and a cancel button in case of a misclaim.
 *
 * The Projector receives the sync events and renders the full
 * cinematic overlay.
 */
export default function BingoCheckPanel() {
  const called = useBingoGameStore((s) => s.called);
  const bingoCheck = useBingoGameStore((s) => s.bingoCheck);
  const confirmedBingo = useBingoGameStore((s) => s.confirmedBingo);
  const startBingoCheck = useBingoGameStore((s) => s.startBingoCheck);
  const verifyNext = useBingoGameStore((s) => s.verifyNextBingoCheck);
  const cancel = useBingoGameStore((s) => s.cancelBingoCheck);
  const dismissConfirmed = useBingoGameStore(
    (s) => s.dismissConfirmedBingo,
  );

  const calledValues = useMemo(
    () => called.map((c) => c.value),
    [called],
  );

  return (
    <div
      data-testid="host-bingo-check-panel"
      className="flex flex-col gap-3"
    >
      <div className="flex items-center gap-2">
        <HeartPulse
          className="w-4 h-4"
          style={{ color: "var(--ic-primary)" }}
        />
        <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)]">
          Bingo Check
        </div>
      </div>

      {confirmedBingo ? (
        <ConfirmedBingoStatus
          info={confirmedBingo}
          onContinue={dismissConfirmed}
        />
      ) : !bingoCheck ? (
        <IdleSetup
          calledValues={calledValues}
          onStart={startBingoCheck}
        />
      ) : (
        <ActiveVerification
          bingoCheck={bingoCheck}
          onVerifyNext={verifyNext}
          onCancel={cancel}
        />
      )}
    </div>
  );
}

/* -------------------- Idle setup -------------------- */

function IdleSetup({ calledValues, onStart }) {
  const [selected, setSelected] = useState([]); // number[] (preserve insertion order)
  const [playerLabel, setPlayerLabel] = useState("");
  const canStart = selected.length >= 3;

  const toggle = (n) => {
    setSelected((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n);
      if (prev.length >= 15) return prev;
      return [...prev, n];
    });
  };

  const clear = () => setSelected([]);

  const handleStart = () => {
    if (!canStart) return;
    onStart(selected, playerLabel);
    setSelected([]);
    setPlayerLabel("");
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-[color:var(--ic-text-muted)] leading-snug">
        Pick the numbers the guest is claiming from the called history
        below (min 3, max 15). The projector will run the heartbeat
        verification when you start.
      </p>

      <Input
        data-testid="bingo-check-player-input"
        placeholder="Winner name or ticket # (optional)"
        value={playerLabel}
        onChange={(e) => setPlayerLabel(e.target.value)}
        className="bg-[color:var(--ic-bg-elevated)] border-[color:var(--ic-border-subtle)]"
      />

      {/* Called-number picker */}
      <div className="rounded-md border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-elevated)] p-2 max-h-56 overflow-y-auto">
        {calledValues.length === 0 ? (
          <div className="text-xs text-[color:var(--ic-text-muted)] py-4 text-center">
            No numbers have been called yet.
          </div>
        ) : (
          <div className="grid grid-cols-6 gap-1.5">
            {calledValues.map((n) => {
              const picked = selected.includes(n);
              return (
                <button
                  key={n}
                  data-testid={`bingo-check-pick-${n}`}
                  type="button"
                  onClick={() => toggle(n)}
                  className="h-8 rounded text-xs font-semibold tabular-nums transition-all"
                  style={{
                    background: picked
                      ? "linear-gradient(180deg, #4ADE80 0%, #16A34A 100%)"
                      : "var(--ic-bg-surface)",
                    color: picked ? "#0a0a0a" : "var(--ic-text-primary)",
                    border: `1px solid ${
                      picked
                        ? "rgba(74, 222, 128, 0.85)"
                        : "var(--ic-border-subtle)"
                    }`,
                    boxShadow: picked
                      ? "0 0 10px rgba(74, 222, 128, 0.45)"
                      : "none",
                  }}
                >
                  {n}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected preview */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)]">
            Selected · {selected.length}
          </span>
          {selected.map((n, i) => (
            <span
              key={`${n}-${i}`}
              data-testid={`bingo-check-selected-${n}`}
              className="text-xs font-semibold tabular-nums px-2 py-1 rounded"
              style={{
                background:
                  "color-mix(in srgb, var(--ic-primary) 22%, transparent)",
                color: "var(--ic-primary)",
                border:
                  "1px solid color-mix(in srgb, var(--ic-primary) 55%, transparent)",
              }}
            >
              {n}
            </span>
          ))}
          <button
            data-testid="bingo-check-clear"
            type="button"
            onClick={clear}
            className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)] hover:text-white inline-flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      <button
        data-testid="bingo-check-start"
        type="button"
        onClick={handleStart}
        disabled={!canStart}
        className="h-10 rounded-md font-semibold text-black transition-all disabled:opacity-40 hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-2"
        style={{
          background: "var(--ic-primary)",
          boxShadow: canStart ? "0 0 20px var(--ic-primary-glow)" : "none",
        }}
      >
        <HeartPulse className="w-4 h-4" strokeWidth={2.5} />
        Begin Verification
      </button>
    </div>
  );
}

/* -------------------- Active verification -------------------- */

function ActiveVerification({ bingoCheck, onVerifyNext, onCancel }) {
  const { numbers, verifiedCount, playerLabel } = bingoCheck;
  const total = numbers.length;
  const isComplete = verifiedCount >= total;
  const nextNumber = isComplete ? null : numbers[verifiedCount];

  return (
    <div className="flex flex-col gap-3">
      <div
        className="rounded-md p-3 border"
        style={{
          background: "var(--ic-bg-elevated)",
          borderColor:
            "color-mix(in srgb, var(--ic-primary) 45%, transparent)",
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)]">
            Verifying · {verifiedCount}/{total}
          </span>
          <span
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: "var(--ic-primary)" }}
          >
            {playerLabel}
          </span>
        </div>

        {!isComplete && nextNumber != null && (
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)]">
              Next
            </span>
            <span
              data-testid="bingo-check-next-number"
              className="ic-font-display text-4xl font-black tabular-nums"
              style={{ color: "white" }}
            >
              {nextNumber}
            </span>
          </div>
        )}

        {/* Number chips row */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {numbers.map((n, i) => {
            const done = i < verifiedCount;
            const active = i === verifiedCount && !isComplete;
            return (
              <span
                key={`${n}-${i}`}
                className="text-xs font-semibold tabular-nums px-2 py-1 rounded"
                style={{
                  background: done
                    ? "linear-gradient(180deg, rgba(74,222,128,0.28) 0%, rgba(16,46,27,0.6) 100%)"
                    : active
                      ? "rgba(220, 38, 38, 0.22)"
                      : "var(--ic-bg-surface)",
                  color: done
                    ? "rgba(220, 252, 231, 0.95)"
                    : "var(--ic-text-primary)",
                  border: `1px solid ${
                    done
                      ? "rgba(74, 222, 128, 0.75)"
                      : active
                        ? "rgba(220, 38, 38, 0.6)"
                        : "var(--ic-border-subtle)"
                  }`,
                }}
              >
                {n}
              </span>
            );
          })}
        </div>
      </div>

      {!isComplete ? (
        <button
          data-testid="bingo-check-confirm-next"
          type="button"
          onClick={onVerifyNext}
          className="h-11 rounded-md font-semibold text-black transition-all hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-2"
          style={{
            background: "linear-gradient(180deg, #4ADE80 0%, #16A34A 100%)",
            boxShadow: "0 0 22px rgba(74,222,128,0.55)",
          }}
        >
          <Check className="w-4 h-4" strokeWidth={3} />
          Confirm Next ({nextNumber})
        </button>
      ) : (
        <div
          data-testid="bingo-check-complete-notice"
          className="h-11 rounded-md inline-flex items-center justify-center gap-2 text-sm"
          style={{
            background:
              "color-mix(in srgb, var(--ic-primary) 20%, transparent)",
            color: "var(--ic-primary)",
            border:
              "1px solid color-mix(in srgb, var(--ic-primary) 55%, transparent)",
          }}
        >
          <Check className="w-4 h-4" strokeWidth={3} />
          Verified — cueing celebration
        </div>
      )}

      <button
        data-testid="bingo-check-cancel"
        type="button"
        onClick={onCancel}
        className="h-9 rounded-md text-sm border border-[color:var(--ic-border-subtle)] hover:border-[color:var(--ic-border-focus)] text-[color:var(--ic-text-secondary)] inline-flex items-center justify-center gap-2"
      >
        <X className="w-4 h-4" />
        Cancel Verification
      </button>
    </div>
  );
}

/* -------------------- Confirmed celebration state -------------------- */

function ConfirmedBingoStatus({ info, onContinue }) {
  return (
    <div
      data-testid="host-confirmed-bingo-status"
      className="flex flex-col gap-3"
    >
      <div
        className="rounded-md p-4 border flex items-center gap-3"
        style={{
          background:
            "linear-gradient(135deg, rgba(251,191,36,0.18) 0%, rgba(120,53,15,0.35) 100%)",
          borderColor:
            "color-mix(in srgb, var(--ic-primary) 55%, transparent)",
          boxShadow: "0 0 22px rgba(251,191,36,0.28)",
        }}
      >
        <Trophy
          className="w-6 h-6 shrink-0"
          style={{ color: "var(--ic-primary)" }}
          strokeWidth={2}
        />
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-primary)]">
            {info.stageLabel}
          </div>
          <div className="ic-font-display font-semibold truncate text-lg">
            {info.name || "Congratulations!"}
          </div>
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)] mt-0.5">
            Celebration on projector
          </div>
        </div>
      </div>
      <button
        data-testid="bingo-continue-show"
        type="button"
        onClick={onContinue}
        className="h-10 rounded-md font-semibold text-black transition-all hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-2"
        style={{
          background: "var(--ic-primary)",
          boxShadow: "0 0 20px var(--ic-primary-glow)",
        }}
      >
        <PlayCircle className="w-4 h-4" strokeWidth={2.5} />
        Continue the Show
      </button>
    </div>
  );
}
